"use client";

import { c as _c } from "react/compiler-runtime";
import { CustomButton } from '@/shared/ui/molecules';
import { Typography } from '@/shared/ui/atoms';
import { BookmarkIcon } from '@/shared/ui/atoms';
import { useState } from 'react';
import { useMutationPostHospitalFavorite } from '@/entities/hospital/hooks/mutations';
import { useRouter } from 'next/navigation';
import { useAuthContext } from '@/app/providers/AuthProviders';
import { useLocale, useTranslations } from 'next-intl';
import { useQueryHospitalFavoriteState } from '@/entities/hospital/hooks/queries';
import { useQueryClient } from '@tanstack/react-query';
import { buildHospitalShortPath } from "@/entities/hospital/lib/detail-route";
import { useHospitalOriginId } from "@/entities/hospital/model/hospital-origin-id";
interface FavoriteButtonProps {
  favorite: boolean;
  /**
   * `chip` — 기존 알약형(목록·하단 바)
   * `tile` — 병원 상세 히어로의 전화·길찾기·공유와 같은 판을 쓰는 정사각 타일
   */
  variant?: "chip" | "tile";
}
const FavoriteButton = t0 => {
  const $ = _c(39);
  const {
    favorite,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "chip" : t1;
  const t = useTranslations();
  const locale = useLocale();
  const router = useRouter();
  const {
    loggedIn
  } = useAuthContext();
  const queryClient = useQueryClient();
  const hospitalId = useHospitalOriginId();
  const [optimisticFavorite, setOptimisticFavorite] = useState(null);
  const {
    data: personalFavorite,
    isPending: isFavoritePending
  } = useQueryHospitalFavoriteState(hospitalId, loggedIn);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setOptimisticFavorite(null);
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== queryClient) {
    t3 = {
      onError: t2,
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: ["hospitalFavorites"]
        });
        setOptimisticFavorite(null);
      }
    };
    $[1] = queryClient;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const {
    mutate: postHospitalFavorite,
    isPending: isMutationPending
  } = useMutationPostHospitalFavorite(t3);
  const isSelected = optimisticFavorite ?? personalFavorite ?? favorite;
  const isFavoriteLoading = loggedIn && isFavoritePending && personalFavorite === undefined;
  let t4;
  if ($[3] !== hospitalId || $[4] !== isFavoritePending || $[5] !== isMutationPending || $[6] !== isSelected || $[7] !== locale || $[8] !== loggedIn || $[9] !== postHospitalFavorite || $[10] !== router) {
    t4 = () => {
      if (!loggedIn) {
        return router.push(`/${locale}/sign-in?redirect=${buildHospitalShortPath(locale, hospitalId)}`);
      }
      if (isFavoritePending || isMutationPending) {
        return;
      }
      setOptimisticFavorite(!isSelected);
      postHospitalFavorite({
        isFavorite: !isSelected,
        hospitalOriginId: hospitalId
      });
    };
    $[3] = hospitalId;
    $[4] = isFavoritePending;
    $[5] = isMutationPending;
    $[6] = isSelected;
    $[7] = locale;
    $[8] = loggedIn;
    $[9] = postHospitalFavorite;
    $[10] = router;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const handleClick = t4;
  let t5;
  if ($[12] !== isFavoriteLoading || $[13] !== isSelected || $[14] !== t) {
    t5 = isFavoriteLoading ? "\u2026" : isSelected ? t("favorite.saved") : t("favorite.save");
    $[12] = isFavoriteLoading;
    $[13] = isSelected;
    $[14] = t;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const label = t5;
  if (variant === "tile") {
    const t6 = loggedIn && (isFavoritePending || isMutationPending);
    const t7 = loggedIn && (isFavoritePending || isMutationPending);
    const t8 = isFavoriteLoading ? "\uC990\uACA8\uCC3E\uAE30 \uC0C1\uD0DC \uD655\uC778 \uC911" : undefined;
    let t9;
    if ($[16] !== isSelected) {
      t9 = <BookmarkIcon size={16} isSelected={isSelected} />;
      $[16] = isSelected;
      $[17] = t9;
    } else {
      t9 = $[17];
    }
    let t10;
    if ($[18] !== label) {
      t10 = <Typography as="span" size={13} lineHeight={18} className="font-medium text-gray-700">{label}</Typography>;
      $[18] = label;
      $[19] = t10;
    } else {
      t10 = $[19];
    }
    let t11;
    if ($[20] !== handleClick || $[21] !== t10 || $[22] !== t6 || $[23] !== t7 || $[24] !== t8 || $[25] !== t9) {
      t11 = <CustomButton className="hospital-hero-action disabled:cursor-wait disabled:opacity-60" onClick={handleClick} disabled={t6} ariaBusy={t7} ariaLabel={t8}>{t9}{t10}</CustomButton>;
      $[20] = handleClick;
      $[21] = t10;
      $[22] = t6;
      $[23] = t7;
      $[24] = t8;
      $[25] = t9;
      $[26] = t11;
    } else {
      t11 = $[26];
    }
    return t11;
  }
  const t6 = `flex items-center gap-1 py-2 px-3 border ${isSelected ? "border-gray-200" : "border-gray-100"} rounded-full disabled:cursor-wait disabled:opacity-60`;
  const t7 = loggedIn && (isFavoritePending || isMutationPending);
  const t8 = loggedIn && (isFavoritePending || isMutationPending);
  const t9 = isFavoriteLoading ? "\uC990\uACA8\uCC3E\uAE30 \uC0C1\uD0DC \uD655\uC778 \uC911" : undefined;
  let t10;
  if ($[27] !== label) {
    t10 = <Typography size={14} lineHeight={20} className="text-gray-700">{label}</Typography>;
    $[27] = label;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== isSelected) {
    t11 = <BookmarkIcon size={16} isSelected={isSelected} />;
    $[29] = isSelected;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== handleClick || $[32] !== t10 || $[33] !== t11 || $[34] !== t6 || $[35] !== t7 || $[36] !== t8 || $[37] !== t9) {
    t12 = <CustomButton className={t6} onClick={handleClick} disabled={t7} ariaBusy={t8} ariaLabel={t9}>{t10}{t11}</CustomButton>;
    $[31] = handleClick;
    $[32] = t10;
    $[33] = t11;
    $[34] = t6;
    $[35] = t7;
    $[36] = t8;
    $[37] = t9;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  return t12;
};
export default FavoriteButton;